"use client"

import { useRef, useState, useEffect } from 'react';
import styles from './Carousel.module.css';
import { useLocale } from 'next-intl';

interface CarouselProps {
    title?: string;
    children: React.ReactNode;
}

export function Carousel({ title, children }: CarouselProps) {
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const [showLeft, setShowLeft] = useState(false);
    const [showRight, setShowRight] = useState(true);

    const locale = useLocale();
    const isRtl = locale === 'ar';

    const [showStart, setShowStart] = useState(false);
    const [showEnd, setShowEnd] = useState(true);

    const handleScroll = () => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;

            const absScroll = Math.abs(scrollLeft);

            setShowStart(absScroll > 0);
            setShowEnd(Math.ceil(absScroll + clientWidth) < scrollWidth);
        }
    };

    useEffect(() => {
        handleScroll();
    }, []);

    const scrollContainer = (direction: 'start' | 'end') => {
        if (scrollRef.current) {
            const scrollAmount = scrollRef.current.clientWidth;
            const sign = direction === 'start' ? -1 : 1;

            const rtlMultiplier = isRtl ? -1 : 1;

            scrollRef.current.scrollBy({
                left: scrollAmount * sign * rtlMultiplier,
                behavior: 'smooth'
            });
        }
    };

    return (
        <div className={styles.carouselSection}>
            {title && <h3 className={styles.sectionTitle}>{title}</h3>}

            <div className={styles.carouselWrapper}>
                <div className={`${styles.edgeBlocker} ${styles.startEdge} ${showStart ? styles.showBlocker : ''}`}></div>
                <div className={`${styles.edgeBlocker} ${styles.endEdge} ${showEnd ? styles.showBlocker : ''}`}></div>

                <button
                    className={`${styles.scrollButton} ${styles.scrollStart} ${!showStart ? styles.hiddenStart : ''} ${isRtl ? styles.rtl : ''}`}
                    onClick={() => scrollContainer('start')}
                    aria-label="Scroll backward"
                >
                    <svg width="8" height="16" viewBox="0 0 8 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3.86314 8.00891L7.54959 12.7305C8.13166 13.4788 8.13166 14.6904 7.54959 15.4388C6.96752 16.1871 6.02512 16.1871 5.42919 15.4388L0.259852 8.81069C-0.0866184 8.36526 -0.0866185 7.63474 0.259852 7.17149L5.44305 0.561247C6.02512 -0.187083 6.96752 -0.187083 7.56345 0.561247C8.14552 1.30958 8.14552 2.52116 7.56345 3.26949L3.86314 8.00891Z" fill="currentColor" />
                    </svg>
                </button>

                <div
                    className={styles.horizontalScroll}
                    ref={scrollRef}
                    onScroll={handleScroll}
                >
                    {children}
                </div>

                <button
                    className={`${styles.scrollButton} ${styles.scrollEnd} ${!showEnd ? styles.hiddenEnd : ''} ${isRtl ? styles.rtl : ''}`}
                    onClick={() => scrollContainer('end')}
                    aria-label="Scroll forward"
                >
                    <svg width="8" height="16" viewBox="0 0 8 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4.13686 7.99109L0.450411 3.26949C-0.131659 2.52116 -0.131659 1.30958 0.450411 0.561247C1.03248 -0.187082 1.97488 -0.187082 2.57081 0.561247L7.74015 7.18931C8.08662 7.63474 8.08662 8.36526 7.74015 8.82851L2.55695 15.4388C1.97488 16.1871 1.03248 16.1871 0.436553 15.4388C-0.145518 14.6904 -0.145518 13.4788 0.436553 12.7305L4.13686 7.99109Z" fill="currentColor" />
                    </svg>
                </button>
            </div>
        </div>
    );
}